// Everything the visit workspace decides that is not a rendering decision.
//
// The desktop surface, the phone surface and the tests all read from HERE, so
// «which tabs exist», «is this ICD code shaped like one», «what does closing warn
// about» and «what does cancelling delete» have exactly one answer each.

import { SECTIONS } from '../../constants/vocabulary.js';

// ── The tab vocabulary, deliberately the doctor app's ─────────────────────
//
// core.md §7.5: the same three regions and the same tab names, so a student who
// later opens the doctor app recognises the screen. `Ctrl+1..4` maps onto this
// array by index, which is why its ORDER is part of the contract.
export const WORK_TABS = ['note', 'diagnoses', 'rx', 'orders'];
export const DEFAULT_WORK_TAB = 'note';
export const isWorkTab = (id) => WORK_TABS.includes(id);

export const COPILOT_TABS = ['ask', 'differential', 'suggestions'];
export const DEFAULT_COPILOT_TAB = 'ask';

/** The phone's one scroll region, addressed by a segmented control. The rail is first. */
export const MOBILE_SEGMENTS = ['record', ...WORK_TABS, 'assistant'];

/** The four SOAP captions, in the order the note is written. */
export const SOAP_SECTIONS = [
  { id: 's', labelKey: 'visit.soap.s', captionKey: 'visit.soap.sCaption' },
  { id: 'o', labelKey: 'visit.soap.o', captionKey: 'visit.soap.oCaption' },
  { id: 'a', labelKey: 'visit.soap.a', captionKey: 'visit.soap.aCaption' },
  { id: 'p', labelKey: 'visit.soap.p', captionKey: 'visit.soap.pCaption' },
];

// ── ICD, optional and only shape-checked ─────────────────────────────────
//
// core.md §5.2: `^[A-Za-z][0-9]{2}(\.[0-9A-Za-z]{1,4})?$`, free text otherwise.
// It is a SHAPE check, not a lookup: this app ships no ICD table, and a student
// practising is allowed to write a code that does not exist yet — they are not
// allowed to write something that is not a code at all.
export const ICD_PATTERN = /^[A-Za-z][0-9]{2}(\.[0-9A-Za-z]{1,4})?$/;
export const isValidIcd = (code) => !code || ICD_PATTERN.test(String(code).trim());

/** The drug picker asks the formulary only from the second character. §7.5. */
export const DRUG_QUERY_MIN = 2;
export const DRUG_QUERY_DEBOUNCE_MS = 300;

/** The rail's sections, in the fixed order of §7.5. `allergies` is locked first. */
export const RAIL_SECTIONS = [
  'allergies', 'identity', 'vitals', 'medications', 'problems', 'pmh', 'labs', 'history',
];

/** Which شرح حال section feeds which rail block. */
export const RAIL_SECTION_OF = {
  allergies: 'allergies',
  vitals: 'vitals',
  pmh: 'pmh',
  labs: 'labs',
};

/** Entries of one history section, newest first. */
export const entriesOf = (record, section) =>
  (record?.case?.entries || [])
    .filter((e) => e.section === section)
    .sort((a, b) => Number(b.recordedAtMs || b.createdAtMs || 0) - Number(a.recordedAtMs || a.createdAtMs || 0));

/** Every history section that has anything in it, in the closed set's order. */
export const writtenSections = (record) =>
  SECTIONS.filter((id) => entriesOf(record, id).length > 0);

/** Does this patient have an allergy on file? The rail's first block depends on it. */
export const hasAllergies = (record) => entriesOf(record, 'allergies').length > 0;

// ── Elapsed time ─────────────────────────────────────────────────────────

/**
 * The header's live clock, as {hours, minutes}. Rendered through the i18n
 * formatter, never as a formatted string built here: this file has no locale.
 */
export function elapsedParts(startedAtMs, now = Date.now()) {
  const started = Number(startedAtMs) || 0;
  if (!started) return { hours: 0, minutes: 0 };
  const delta = Math.max(0, now - started);
  const totalMinutes = Math.floor(delta / 60000);
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}

// ── Closure ──────────────────────────────────────────────────────────────

/** The first line of a paragraph, trimmed — the seed of the closing summary. */
const firstLine = (text) => String(text || '').split('\n').map((l) => l.trim()).find(Boolean) || '';

/**
 * «خلاصهٔ ویزیت» pre-filled from the first line of A and the first line of P.
 * A student who writes nothing else has still recorded an assessment and a plan.
 */
export function summarySeed(soap) {
  return [firstLine(soap?.a), firstLine(soap?.p)].filter(Boolean).join(' — ');
}

/**
 * What the closure dialog WARNS about. Every one of these is a warning and none
 * of them blocks: core.md §6 rule 3 — «Closing needs nothing».
 */
export function closureWarnings({ soap, diagnoses = [], openSheets = 0 } = {}) {
  const out = [];
  if (!['s', 'o', 'a', 'p'].some((f) => (soap?.[f] || '').trim())) out.push('emptyNote');
  if (!diagnoses.length) out.push('noDiagnoses');
  if (openSheets > 0) out.push('openSuggestions');
  return out;
}

/**
 * What cancelling deletes, as counts — the confirmation names them out loud
 * («۲ تشخیص، ۳ دارو، ۱ دستور حذف می‌شود», core.md §6 rule 5). History entries are
 * NOT counted: they survive with `encounter_id = NULL`.
 */
export function cancelCounts(encounter) {
  return {
    diagnoses: (encounter?.diagnoses || []).length,
    rx: (encounter?.rxItems || []).length,
    orders: (encounter?.orders || []).length,
  };
}

/** Orders grouped by type, in the catalog's tab order. */
export const ORDER_GROUPS = ['lab', 'imaging', 'procedure', 'referral', 'other'];

export function groupOrders(orders = []) {
  const groups = new Map(ORDER_GROUPS.map((t) => [t, []]));
  for (const o of orders) {
    const key = groups.has(o.orderType) ? o.orderType : 'other';
    groups.get(key).push(o);
  }
  return [...groups.entries()]
    .filter(([, rows]) => rows.length)
    .map(([type, rows]) => ({ type, rows }));
}

/** The catalog, flattened into one searchable list with its type on every row. */
export function flattenCatalog(catalog) {
  if (!catalog) return [];
  return [
    ...(catalog.labs || []),
    ...(catalog.imaging || []),
    ...(catalog.procedures || []),
  ].map((row) => ({ ...row, orderType: row.orderType || 'other' }));
}

/** A prescription line as one string — the print view and the table both use it. */
export function rxLine(item) {
  return [item?.dose, item?.frequency, item?.duration].filter(Boolean).join(' · ');
}

/**
 * The identity block the printed prescription carries: the STUDENT, and never a
 * کد نظام پزشکی. core.md §7.5 — a practice prescription that carried a licence
 * field would be one field away from looking valid.
 */
export function printIdentity(me) {
  return {
    fullName: me?.fullName || '',
    level: me?.level || '',
    university: me?.university || '',
    studentNumber: me?.studentNumber || '',
  };
}
